import { useEffect, useRef, useState } from "react";
import { AppLayout } from "@/components/AppLayout";
import { api, formatApiErrorDetail } from "@/lib/api";
import { formatRupiah } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Eye, MagnifyingGlass, UploadSimple, Check } from "@phosphor-icons/react";
import { MonthYearPicker } from "@/components/inputs";
import {
  RFQStatusBadge, RFQInfoGrid, RFQItemsTable, RFQItemDetailModal, RFQQuotationBox, RFQTimeline,
} from "@/components/RFQDetail";
import { RFQ_DEPT_SUGGESTIONS, fmtDateTime } from "@/lib/rfq";

export default function RFQAdminPage() {
  const wibDate = new Date().toLocaleDateString("en-GB", { timeZone: "Asia/Jakarta" }).split("/").map(Number);
  const [, currentMonth, currentYear] = wibDate;
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [acting, setActing] = useState(false);
  const [notes, setNotes] = useState("");
  const quoteRef = useRef(null);
  const [filters, setFilters] = useState({ q: "", status: "all", department: "", month: currentMonth, year: currentYear });

  const load = (f = filters) => {
    setLoading(true);
    const params = {};
    if (f.q.trim()) params.q = f.q.trim();
    if (f.status !== "all") params.status = f.status;
    if (f.department.trim()) params.department = f.department.trim();
    params.month = f.month;
    params.year = f.year;
    api.get("/rfqs", { params }).then((res) => {
      const items = res.data.filter((rfq) => {
        const [, requestMonth, requestYear] = String(rfq.request_date || "").split("/").map(Number);
        return requestMonth === Number(f.month) && requestYear === Number(f.year);
      });
      setList(items);
      setDetail((prev) => (prev ? items.find((x) => x.id === prev.id) || null : prev));
    }).catch((err) => {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const openDetail = async (r) => {
    try {
      const res = await api.get(`/rfqs/${r.id}`);
      setDetail(res.data);
      setNotes(res.data.admin_notes || "");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const refreshDetail = async (id) => {
    const res = await api.get(`/rfqs/${id}`);
    setDetail(res.data);
    setNotes(res.data.admin_notes || "");
    load();
  };

  const setStatus = async (status) => {
    if (!detail) return;
    setActing(true);
    try {
      await api.put(`/rfqs/${detail.id}`, { status });
      toast.success("Status diubah menjadi Completed");
      await refreshDetail(detail.id);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setActing(false);
    }
  };

  const saveNotes = async () => {
    if (!detail) return;
    setActing(true);
    try {
      await api.put(`/rfqs/${detail.id}`, { admin_notes: notes });
      toast.success("Admin notes disimpan");
      await refreshDetail(detail.id);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setActing(false);
    }
  };

  const uploadQuotation = async (file) => {
    if (!file || !detail) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const up = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await api.put(`/rfqs/${detail.id}`, { quotation_path: up.data.path });
      toast.success("File quotation terupload");
      await refreshDetail(detail.id);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setUploading(false);
    }
  };

  const downloadQuotation = async (rfq) => {
    setDownloading(true);
    try {
      const res = await api.get(`/rfqs/${rfq.id}/quotation`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = rfq.quotation?.file_name || "quotation";
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch (e) {
      toast.error("Gagal mengunduh quotation");
    } finally {
      setDownloading(false);
    }
  };

  const applyFilters = () => load();
  const resetFilters = () => {
    const f = { q: "", status: "all", department: "", month: currentMonth, year: currentYear };
    setFilters(f);
    load(f);
  };

  const totalCount = list.length;
  const submittedCount = list.filter((r) => r.status === "submitted").length;
  const completedCount = list.filter((r) => r.status === "completed").length;

  return (
    <AppLayout title="RFQ Management">
      <div className="max-w-6xl space-y-6 animate-fade-up">
        <div>
          <p className="text-[14px] text-[#64748B]">Admin Panel <span className="mx-1">›</span> <span className="text-[#006398] font-medium">RFQ Management &amp; Pengadaan</span></p>
          <h1 className="skn-h1 text-[28px] md:text-[36px] leading-tight mt-1">RFQ Management &amp; Pengadaan</h1>
          <p className="text-[14px] text-[#64748B] mt-1">Kelola permohonan penawaran harga vendor dan alur persetujuan.</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="p-5 rounded-xl bg-white shadow-sm">
            <div className="flex items-start justify-between">
              <div>
                <p className="skn-section-label">Total RFQ Periode Ini</p>
                <p className="text-[28px] font-bold mt-2 text-[#00364e] tabular-nums" style={{ fontFamily: '"Plus Jakarta Sans", sans-serif' }}>{loading ? "…" : totalCount}</p>
              </div>
              <div className="w-11 h-11 rounded-xl bg-[#e5eeff] flex items-center justify-center text-[#00364e]"><span className="material-symbols-outlined text-[24px]">assignment_turned_in</span></div>
            </div>
          </div>
          <div className="p-5 rounded-xl bg-white shadow-sm">
            <div className="flex items-start justify-between">
              <div>
                <p className="skn-section-label">Menunggu Persetujuan</p>
                <p className="text-[28px] font-bold mt-2 text-[#D97706] tabular-nums" style={{ fontFamily: '"Plus Jakarta Sans", sans-serif' }}>{loading ? "…" : submittedCount}</p>
              </div>
              <div className="w-11 h-11 rounded-xl bg-amber-50 flex items-center justify-center text-[#D97706]"><span className="material-symbols-outlined text-[24px]">pending_actions</span></div>
            </div>
          </div>
          <div className="p-5 rounded-xl bg-white shadow-sm">
            <div className="flex items-start justify-between">
              <div>
                <p className="skn-section-label">Selesai</p>
                <p className="text-[28px] font-bold mt-2 text-[#059669] tabular-nums" style={{ fontFamily: '"Plus Jakarta Sans", sans-serif' }}>{loading ? "…" : completedCount}</p>
              </div>
              <div className="w-11 h-11 rounded-xl bg-emerald-50 flex items-center justify-center text-[#059669]"><span className="material-symbols-outlined text-[24px]">task_alt</span></div>
            </div>
          </div>
        </div>
        <Card className="p-4 bg-white border-[#E2E8F0]/80 shadow-sm">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
            <div className="space-y-1.5 lg:col-span-2">
              <Label>Search (RFQ Number / Nama / Item)</Label>
              <div className="relative">
                <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input
                  value={filters.q}
                  onChange={(e) => setFilters({ ...filters, q: e.target.value })}
                  onKeyDown={(e) => e.key === "Enter" && applyFilters()}
                  placeholder="Cari…"
                  className="pl-9"
                  data-testid="rfq-search-input"
                />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Status</Label>
              <Select value={filters.status} onValueChange={(v) => setFilters({ ...filters, status: v })}>
                <SelectTrigger data-testid="rfq-status-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Semua</SelectItem>
                  <SelectItem value="submitted">Submitted</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Department</Label>
              <Input
                value={filters.department}
                onChange={(e) => setFilters({ ...filters, department: e.target.value })}
                placeholder="Semua"
                list="rfq-admin-dept"
                data-testid="rfq-department-filter"
              />
              <datalist id="rfq-admin-dept">
                {RFQ_DEPT_SUGGESTIONS.map((d) => <option key={d} value={d} />)}
              </datalist>
            </div>
            <div className="space-y-1.5">
              <Label>Periode Request</Label>
              <MonthYearPicker
                month={filters.month}
                year={filters.year}
                onChange={(month, year) => {
                  const next = { ...filters, month, year };
                  setFilters(next);
                  load(next);
                }}
                years={[currentYear - 2, currentYear - 1, currentYear, currentYear + 1]}
                monthTestId="rfq-month-filter"
                yearTestId="rfq-year-filter"
              />
            </div>
          </div>
          <div className="flex gap-2 mt-3">
            <Button onClick={applyFilters} data-testid="rfq-filter-button">Terapkan</Button>
            <Button variant="outline" onClick={resetFilters} data-testid="rfq-filter-reset">Reset</Button>
          </div>
        </Card>

        <Card className="overflow-hidden overflow-x-auto bg-white border-[#E2E8F0]/80 shadow-sm">
          <div className="px-5 py-3 bg-[#eff4ff]/70 flex items-center justify-between">
            <span className="text-[16px] font-bold text-[#00364e]" style={{ fontFamily: '"Plus Jakarta Sans", sans-serif' }}>Daftar Pengajuan RFQ</span>
            <span className="text-[14px] text-[#64748B]">{loading ? "Memuat…" : `${list.length} RFQ periode ini`}</span>
          </div>
          <Table>
            <TableHeader>
              <TableRow className="bg-[#eff4ff]">
                <TableHead className="text-[11px] uppercase tracking-wider text-[#64748B]">RFQ Number</TableHead>
                <TableHead className="text-[11px] uppercase tracking-wider text-[#64748B]">Tgl Request</TableHead>
                <TableHead className="text-[11px] uppercase tracking-wider text-[#64748B]">Karyawan</TableHead>
                <TableHead className="text-[11px] uppercase tracking-wider text-[#64748B]">Dept</TableHead>
                <TableHead className="text-center text-[11px] uppercase tracking-wider text-[#64748B]">Item</TableHead>
                <TableHead className="text-right text-[11px] uppercase tracking-wider text-[#64748B]">Grand Total</TableHead>
                <TableHead className="text-[11px] uppercase tracking-wider text-[#64748B]">Status</TableHead>
                <TableHead className="text-[11px] uppercase tracking-wider text-[#64748B]">Last Update</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={9} className="text-center py-10 text-muted-foreground">Memuat…</TableCell></TableRow>
              ) : list.length === 0 ? (
                <TableRow><TableCell colSpan={9} className="text-center py-10 text-muted-foreground">Tidak ada RFQ yang cocok.</TableCell></TableRow>
              ) : list.map((r, idx) => (
                <TableRow key={r.id} className="cursor-pointer hover:bg-[#eff4ff]/60" onClick={() => openDetail(r)} data-testid={`admin-rfq-row-${idx}`}>
                  <TableCell className="font-bold text-[#00364e]">{r.rfq_number}</TableCell>
                  <TableCell>{r.request_date}</TableCell>
                  <TableCell className="font-medium text-[#0b1c30]">{r.employee_name}</TableCell>
                  <TableCell className="text-[#64748B]">{r.department}</TableCell>
                  <TableCell className="text-center">{r.item_count}</TableCell>
                  <TableCell className="text-right tabular-nums font-medium text-[#00364e]">{formatRupiah(r.grand_total)}</TableCell>
                  <TableCell><RFQStatusBadge status={r.status} /></TableCell>
                  <TableCell className="text-sm text-muted-foreground">{fmtDateTime(r.updated_at)}</TableCell>
                  <TableCell>
                    <Button variant="ghost" size="sm" className="gap-1.5" onClick={(e) => { e.stopPropagation(); openDetail(r); }} data-testid={`view-admin-rfq-${idx}`}>
                      <Eye size={16} /> Detail
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>

        <Dialog open={!!detail} onOpenChange={(v) => !v && setDetail(null)}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="admin-rfq-detail-modal">
            <DialogHeader><DialogTitle>Kelola {detail?.rfq_number}</DialogTitle></DialogHeader>
            {detail && (
              <div className="space-y-5">
                <div>
                  <h4 className="font-heading font-bold mb-2">Request Information</h4>
                  <RFQInfoGrid rfq={detail} />
                </div>
                <div>
                  <h4 className="font-heading font-bold mb-2">Item Details</h4>
                  <p className="text-xs text-muted-foreground mb-2">Klik baris untuk melihat detail item.</p>
                  <Card className="overflow-hidden overflow-x-auto">
                    <RFQItemsTable
                      items={detail.items}
                      onSelect={(item, index) => setSelectedItem({ item, index })}
                    />
                  </Card>
                  <div className="flex items-center justify-between mt-2">
                    <span className="text-sm font-bold">Grand Total</span>
                    <span className="font-heading font-bold tabular-nums text-primary">{formatRupiah(detail.grand_total)}</span>
                  </div>
                </div>

                <div className="rounded-xl border border-border p-4 space-y-3">
                  <h4 className="font-heading font-bold">Admin Process</h4>
                  <div className="space-y-1.5">
                    <Label>Admin Notes (terlihat oleh karyawan)</Label>
                    <Textarea
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      placeholder='Contoh: RFQ sedang dimintakan quotation kepada vendor.'
                      data-testid="admin-notes-input"
                    />
                    <Button variant="outline" size="sm" onClick={saveNotes} disabled={acting || notes === (detail.admin_notes || "")} data-testid="save-admin-notes-button">
                      Simpan Catatan
                    </Button>
                  </div>
                  <div className="space-y-1.5">
                    <Label>File Quotation (PDF / XLS / XLSX / DOC / DOCX, maks 5MB)</Label>
                    <div className="flex items-center gap-2">
                      <input
                        ref={quoteRef}
                        type="file"
                        accept=".pdf,.xls,.xlsx,.doc,.docx"
                        className="hidden"
                        data-testid="upload-quotation-input"
                        onChange={(e) => { uploadQuotation(e.target.files?.[0]); e.target.value = ""; }}
                      />
                      <Button variant="outline" size="sm" className="gap-1.5" disabled={uploading} onClick={() => quoteRef.current?.click()} data-testid="upload-quotation-button">
                        <UploadSimple size={16} /> {uploading ? "Mengupload…" : detail.quotation ? "Ganti File" : "Upload File"}
                      </Button>
                      {detail.quotation && <span className="text-xs text-muted-foreground">{detail.quotation.file_name}</span>}
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2 pt-1">
                    {detail.status !== "completed" && (
                      <Button
                        onClick={() => setStatus("completed")}
                        disabled={acting || !detail.quotation}
                        title={!detail.quotation ? "Upload file quotation terlebih dahulu" : undefined}
                        variant="success"
                        className="gap-2"
                        data-testid="rfq-to-completed-button"
                      >
                        <Check size={16} weight="bold" /> Selesaikan → Completed
                      </Button>
                    )}
                    {detail.status === "completed" && (
                      <span className="text-sm text-[hsl(var(--success))] font-medium">RFQ sudah completed.</span>
                    )}
                  </div>
                </div>

                <RFQQuotationBox rfq={detail} onDownload={() => downloadQuotation(detail)} downloading={downloading} />
                <RFQTimeline history={detail.history} />
              </div>
            )}
          </DialogContent>
        </Dialog>

        <RFQItemDetailModal
          item={selectedItem?.item}
          index={selectedItem?.index}
          onClose={() => setSelectedItem(null)}
        />
      </div>
    </AppLayout>
  );
}
